/**
 * Web presentation wrapper around zen-core-elements' ZenInput.
 *
 * The shared component is *not* modified - this file only supplies web
 * defaults for the style props it already accepts, and webpack aliases
 * `zen-core-elements` to the barrel that exports this instead of the original
 * (see webpack.config.js). Android keeps importing the untouched component
 * through Metro.
 *
 * The problem being fixed (spec 13.1):
 *
 *   ZenInput always renders its error <Text>, even when there is no message
 *   (`content={errorMessage || ''}`). On native an empty Text still occupies a
 *   line box, so the validation slot exists whether or not it is filled and
 *   neighbouring fields never move. react-native-web renders the same empty
 *   Text as an empty <div>, which has zero height - so the field grows by a
 *   whole line the instant a message appears, and every control underneath it
 *   jumps down. Reserving the line with minHeight restores the native
 *   behaviour on web.
 *
 * Defaults are applied *before* the caller's styles in every array, so any
 * screen that already tuned a field keeps winning. The one exception is the
 * reserved error height, which is applied last - it is the fix itself, and no
 * screen sets minHeight on an error message.
 */

import React, {forwardRef} from 'react';
import {StyleSheet, TextInput} from 'react-native';
import BaseZenInput, {
  ZenInputProps,
} from 'zen-core-elements/src/elements/zenInput/zenInput';

import {webControl, webField, webFontFamily, webFontSize} from './webTokens';

const styles = StyleSheet.create({
  label: {
    fontFamily: webFontFamily.regular,
    fontSize: webField.labelFontSize,
    lineHeight: webField.labelLineHeight,
    marginBottom: webField.labelMarginBottom,
  },

  /**
   * minHeight rather than height: a `multiline` field still has to be able to
   * grow, and screens that set an explicit height keep theirs.
   */
  inputContainer: {
    minHeight: webControl.boxHeight,
    alignItems: 'center',
  },

  /**
   * No outline properties here on purpose.
   *
   * The focus ring is owned by globalStyles.web.ts (:focus / :focus-visible)
   * so that every focusable control looks the same, not just the ones that
   * happen to go through ZenInput. Declaring outline here would compile to a
   * react-native-web atomic class that competes with that stylesheet for no
   * benefit, and it would apply in the unfocused state too.
   */
  input: {
    fontFamily: webFontFamily.regular,
    fontSize: webFontSize.input,
    paddingHorizontal: webControl.paddingHorizontal,
  },

  error: {
    fontFamily: webFontFamily.regular,
    fontSize: webField.errorFontSize,
    lineHeight: webField.errorLineHeight,
    marginTop: webField.errorMarginTop,
    /**
     * ZenInput gives the error text `alignSelf: 'flex-start'`, which in a
     * column flex container makes the box shrink to the text's own intrinsic
     * width. Measured in the browser, "First name is required" ends up in a
     * 144px box - its own max-content width, to the pixel - and sub-pixel
     * rounding then wraps the last word onto a second line. The slot doubles
     * in height and the rest of the form drops by a line, even though the
     * field is 735px wide and the message obviously fits.
     *
     * Stretching the box to the field's width is what makes a one-line
     * message stay one line.
     */
    alignSelf: 'stretch',
    width: '100%',
  },

  /**
   * The reserved validation slot, with its box geometry pinned so the height
   * is decided here and nowhere else.
   *
   * minHeight holds one line open at all times, so showing or clearing a
   * message never changes the field's height. maxHeight caps a long message at
   * two lines: past that the row would start redistributing the form, which is
   * precisely what 13.4 asks to prevent.
   *
   * Two separate habits in the screens make the padding/margin resets
   * necessary.
   *
   * 1. Unconditional padding. addEditCustomer sets paddingVertical: 10 on its
   *    errorStyle. While the slot is empty that padding *is* the reserved
   *    space, so the form looks stable - but the moment a message arrives its
   *    18px line box stacks on top and the field grows to 38px.
   *
   * 2. Conditional padding, which is the same layout shift written on
   *    purpose:
   *
   *      paddingBottom: errors.groupName && touched.groupName ? 20 : 0
   *
   *    addEditCustomerGroups, productGeneral, addEditBuyXgetYDiscount (x2)
   *    and signIn all do a version of this. The style literally changes shape
   *    when the message appears.
   *
   * Both the shorthand *and* the longhands are declared, all zero. A
   * shorthand and a longhand for the same edge do not resolve by array order
   * in react-native-web, and which one wins depends on which side declared
   * which: with only `paddingVertical: 0` here, Customer Group's
   * `paddingBottom: 20` won (36px); with only the longhands, Customer's
   * `paddingVertical: 10` won (36px again). Declaring every spelling means
   * the answer is 0 whichever the resolver picks.
   *
   * Margins get the same treatment because they shift siblings even though
   * they sit outside the measured box. The 2px gap under the input is a
   * nice-to-have and is written last; if the resolver prefers the shorthand
   * the gap collapses to 0, which costs two pixels and keeps the slot a
   * constant height either way. Constant is the requirement.
   */
  errorReservedSpace: {
    paddingVertical: 0,
    paddingTop: 0,
    paddingBottom: 0,
    marginVertical: 0,
    marginBottom: 0,
    marginTop: webField.errorMarginTop,
    minHeight: webField.errorMinHeight,
    maxHeight: webField.errorLineHeight * webField.errorMaxLines,
    overflow: 'hidden',
  },

  /**
   * The sign-in email field is the one exception to the reserved slot. Its
   * only neighbour is the Next button, so there is nothing below it to jump,
   * and an empty 20px slot there just reads as a gap that is too big. With no
   * message the slot is dropped entirely; the message, when it comes, pushes
   * Next down the way it does on Android.
   */
  errorCollapsed: {
    display: 'none',
  },
});

/**
 * Sign-in is recognised by its label style: signIn.tsx passes the theme's
 * `signInStyle.labelStyle`, which webOverrides.web.ts leaves untouched, so it
 * reaches here as the very object signInStyle.ts created. No other field uses
 * it, and the shared screen needs no change to be identified.
 */
function isSignInField(labelStyle: ZenInputProps['labelStyle']) {
  /*
   * Required lazily, at render time. A top-level import loads the theme while
   * this wrapper is itself still loading - zen-core-elements is reached from
   * inside appTheme's own import chain (appTheme -> store -> ... ->
   * zen-core-elements -> here) - so signInStyle was built before appTheme had
   * defined appColors, and the sign-in screen lost all its colours.
   */
  const {signInStyle} = require('../../themes/components/auth/signInStyle');
  return labelStyle === signInStyle.labelStyle;
}

/**
 * `dataSet` is a react-native-web extension (it renders data-* attributes)
 * that the shared react-native prop types do not declare, hence the cast.
 * ZenInput spreads unknown props straight onto its TextInput.
 */
function fieldTag() {
  return {dataSet: {zenField: 'true'}} as Partial<ZenInputProps>;
}

const WebZenInput = forwardRef<TextInput, ZenInputProps>(
  (
    {labelStyle, inputContainerStyle, inputStyle, errorStyle, onEndEditing, onBlur, ...props},
    ref,
  ) => (
    <BaseZenInput
      ref={ref}
      {...props}
      /*
       * react-native-web's TextInput never fires onEndEditing, yet ~35 fields
       * hang real work on it: trimming emails/phones before validation, the
       * duplicate-option check that guards Save, clearing the focus border.
       * Native fires it as editing ends, just before onBlur - so do that here.
       */
      onBlur={
        onEndEditing || onBlur
          ? (event: any) => {
              onEndEditing?.(event);
              onBlur?.(event);
            }
          : undefined
      }
      labelStyle={[styles.label, labelStyle]}
      inputContainerStyle={[styles.inputContainer, inputContainerStyle]}
      /**
       * Marks the rendered <input> so the stylesheet can find the *field
       * box* around it (globalStyles.web.ts, via :has). The focus ring has
       * to sit on that wrapper, not on the bare input: ZenInput pads the
       * wrapper, so the input is inset ~9px from the border on the left and
       * flush with it top and bottom. An outline on the input therefore
       * draws a second, misaligned rectangle right on top of the field
       * border. Measured: wrapper 120,227 705x42 vs input 129,228 687x40.
       */
      {...fieldTag()}
      inputStyle={[styles.input, inputStyle]}
      /*
       * errorReservedSpace goes last on purpose - see the block comment above.
       * ZenInput still appends its own `display: none` after this array when
       * `renderErrorMessage={false}`, so opting out of the slot entirely keeps
       * working.
       */
      errorStyle={[
        styles.error,
        errorStyle,
        styles.errorReservedSpace,
        isSignInField(labelStyle) && !props.errorMessage && styles.errorCollapsed,
      ]}
    />
  ),
);

WebZenInput.displayName = 'WebZenInput';

export default WebZenInput;
